import React from "react";
import { Text } from "@mantine/core";
import {
  DataTable,
  type DataTableColumn,
  type DataTableFooterCell,
  type DataTableInfiniteScroll,
  type DataTablePagination,
} from "@xplatform/design-system";

export interface AdminTableProps<T> {
  /** Declare columns with the `col.*` factories so each kind looks the same everywhere. */
  columns: Array<DataTableColumn<T>>;
  data: T[];
  getRowKey: (row: T, index: number) => string | number;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  emptyMessage?: string;
  emptyState?: React.ReactNode;
  footer?: DataTableFooterCell[];
  pagination?: DataTablePagination;
  infiniteScroll?: DataTableInfiniteScroll;
  maxHeight?: number | string;
}

/**
 * The one table of the admin module: the design-system DataTable with the
 * admin's density and layout fixed in one place. Fixed column layout (widths
 * come from the column kind, never from content), one-line headers in the
 * dimmed `xs` label style, `sm` row spacing, hover highlight. Pages put it in a
 * SectionCard; it adds no container or spacing of its own.
 */
export function AdminTable<T>({ columns, data, getRowKey, onRowClick, loading, emptyMessage, emptyState, footer,
                                pagination, infiniteScroll, maxHeight }: AdminTableProps<T>) {
  const headed = columns.map((column) => ({
    ...column,
    header: typeof column.header === "string" && column.header
      ? <Text size="xs" fw={600} c="dimmed" tt="uppercase" truncate title={column.header}>{column.header}</Text>
      : column.header,
  }));
  return (
    <DataTable
      columns={headed}
      data={data}
      getRowKey={getRowKey}
      onRowClick={onRowClick ? (row) => onRowClick(row) : undefined}
      loading={loading && !data.length}
      emptyMessage={emptyMessage}
      emptyState={emptyState}
      footer={footer}
      pagination={pagination}
      infiniteScroll={infiniteScroll}
      maxHeight={maxHeight}
      layout="fixed"
      verticalSpacing="sm"
      horizontalSpacing="md"
      highlightOnHover
    />
  );
}
